import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Eye, EyeOff, PackagePlus } from "lucide-react";
import { Modal, Field, inputCls, Btn, ConfirmDialog, AdminImageUpload } from "./kit";
import StockBadge from "@/components/public/StockBadge";

const EMPTY = {
  product_name: "", category: "Indonesian Arabica", coffee_type: "Arabica", origin: "", region: "",
  variety: "", process: "", altitude: "", grade: "", screen_size: "", moisture: "", cupping_notes: "",
  crop_year: "", available_stock: 0, stock_unit: "MT", moq: "", packaging: "", price_status: "On Request",
  stock_status: "AVAILABLE", description: "", image: "", featured: false, visible: true, low_stock_threshold: 0,
};
const STATUS = ["AVAILABLE", "LIMITED", "ON_REQUEST", "SOLD_OUT"];
const UNITS = ["MT", "KG", "BAGS"];
const FIELDS = [
  ["origin", "Origin"], ["region", "Region"], ["variety", "Variety"], ["process", "Process"],
  ["altitude", "Altitude"], ["grade", "Grade"], ["screen_size", "Screen Size"], ["moisture", "Moisture"],
  ["crop_year", "Crop Year"], ["moq", "MOQ"], ["packaging", "Packaging"], ["price_status", "Price Status"],
];

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(false);
  const [stockModal, setStockModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { try { const r = await api.get("/admin/products"); setItems(r.data); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing(null); setModal(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p.id); setModal(true); };

  const save = async () => {
    if (!form.product_name) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, available_stock: parseFloat(form.available_stock) || 0, low_stock_threshold: parseFloat(form.low_stock_threshold) || 0 };
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setModal(false); load();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const del = async () => { try { await api.delete(`/admin/products/${confirm}`); toast.success("Deleted"); setConfirm(null); load(); } catch (e) { toast.error("Failed"); } };
  const toggleVisible = async (p) => { try { await api.put(`/admin/products/${p.id}`, { visible: !p.visible }); load(); } catch (e) {} };

  const saveStock = async () => {
    try { await api.put(`/admin/products/${stockModal.id}`, { available_stock: parseFloat(stockModal.available_stock) || 0, stock_status: stockModal.stock_status }); toast.success("Stock updated"); setStockModal(null); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const filtered = items.filter((p) => !q || [p.product_name, p.origin, p.process].join(" ").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div><h1 className="font-display text-2xl font-extrabold text-offwhite">Inventory</h1><p className="mt-1 font-body text-sm text-offwhite/55">Manage coffee products and stock.</p></div>
        <Btn onClick={openNew} data-testid="add-coffee-btn"><Plus className="mr-1 inline h-4 w-4" /> Add New Coffee</Btn>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-offwhite/40" />
        <input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className={`${inputCls} pl-10`} />
      </div>

      <div className="overflow-x-auto rounded-xl border border-offwhite/10 bg-[#101c14]">
        <table className="w-full min-w-[860px] text-left">
          <thead className="border-b border-offwhite/10">
            <tr className="font-mono text-[10px] uppercase tracking-wider text-offwhite/50">
              {["Product", "Origin", "Process", "Stock", "Status", "Last Updated", "Actions"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center font-body text-sm text-offwhite/40">No products. Click "Add New Coffee" or load demo data.</td></tr>
            ) : filtered.map((p) => (
              <tr key={p.id} data-testid={`inventory-row-${p.id}`} className="border-b border-offwhite/5 font-body text-sm hover:bg-offwhite/[0.03]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-offwhite">{p.product_name}</span>
                    {p.featured && <span className="rounded bg-amber-brand/20 px-1.5 py-0.5 font-mono text-[9px] uppercase text-amber-soft">Featured</span>}
                    {p.is_demo && <span className="rounded bg-offwhite/10 px-1.5 py-0.5 font-mono text-[9px] uppercase text-offwhite/50">Demo</span>}
                    {!p.visible && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 font-mono text-[9px] uppercase text-rose-300">Hidden</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-offwhite/70">{p.region || p.origin || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-offwhite/70">{p.process || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-offwhite/70">{p.available_stock} {p.stock_unit}</td>
                <td className="px-4 py-3"><StockBadge status={p.stock_status} /></td>
                <td className="px-4 py-3 font-mono text-[10px] text-offwhite/45">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setStockModal({ ...p })} title="Update stock" data-testid={`update-stock-${p.id}`} className="rounded p-1.5 text-emerald-300 hover:bg-offwhite/10"><PackagePlus className="h-4 w-4" /></button>
                    <button onClick={() => openEdit(p)} title="Edit" data-testid={`edit-${p.id}`} className="rounded p-1.5 text-offwhite/70 hover:bg-offwhite/10"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => toggleVisible(p)} title={p.visible ? "Hide" : "Show"} data-testid={`toggle-visible-${p.id}`} className="rounded p-1.5 text-offwhite/70 hover:bg-offwhite/10">{p.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                    <button onClick={() => setConfirm(p.id)} title="Delete" data-testid={`delete-${p.id}`} className="rounded p-1.5 text-rose-300 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Coffee" : "Add New Coffee"} wide>
        <div className="max-h-[68vh] space-y-4 overflow-y-auto pr-1">
          <Field label="Product Name *"><input data-testid="form-product-name" className={inputCls} value={form.product_name} onChange={(e) => set("product_name", e.target.value)} /></Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Category"><input className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
            <Field label="Stock Status"><select data-testid="form-stock-status" className={inputCls} value={form.stock_status} onChange={(e) => set("stock_status", e.target.value)}>{STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
            <Field label="Unit"><select className={inputCls} value={form.stock_unit} onChange={(e) => set("stock_unit", e.target.value)}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Available Stock"><input type="number" data-testid="form-stock" className={inputCls} value={form.available_stock} onChange={(e) => set("available_stock", e.target.value)} /></Field>
            <Field label="Low Stock Threshold"><input type="number" className={inputCls} value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} /></Field>
            <div />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {FIELDS.map(([k, lbl]) => (
              <Field key={k} label={lbl}><input className={inputCls} value={form[k]} onChange={(e) => set(k, e.target.value)} /></Field>
            ))}
          </div>
          <Field label="Cupping Notes"><textarea rows={2} className={inputCls} value={form.cupping_notes} onChange={(e) => set("cupping_notes", e.target.value)} /></Field>
          <Field label="Description"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <AdminImageUpload label="Product Image" value={form.image} onChange={(v) => set("image", v)} testId="form-image-upload" />
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 font-body text-sm text-offwhite/80"><input type="checkbox" data-testid="form-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-amber-brand" /> Featured</label>
            <label className="flex items-center gap-2 font-body text-sm text-offwhite/80"><input type="checkbox" data-testid="form-visible" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} className="h-4 w-4 accent-forest" /> Visible on website</label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3 border-t border-offwhite/10 pt-4">
          <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="save-product-btn">{saving ? "Saving…" : "Save"}</Btn>
        </div>
      </Modal>

      {/* Quick stock modal */}
      <Modal open={!!stockModal} onClose={() => setStockModal(null)} title="Update Stock">
        {stockModal && (
          <div className="space-y-4">
            <div className="font-display text-base font-bold text-offwhite">{stockModal.product_name}</div>
            <Field label={`Available Stock (${stockModal.stock_unit})`}><input type="number" data-testid="quick-stock-input" className={inputCls} value={stockModal.available_stock} onChange={(e) => setStockModal({ ...stockModal, available_stock: e.target.value })} /></Field>
            <Field label="Stock Status"><select data-testid="quick-status-select" className={inputCls} value={stockModal.stock_status} onChange={(e) => setStockModal({ ...stockModal, stock_status: e.target.value })}>{STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
            <div className="flex justify-end gap-3"><Btn variant="ghost" onClick={() => setStockModal(null)}>Cancel</Btn><Btn onClick={saveStock} data-testid="save-stock-btn">Update</Btn></div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={del} message="Delete this product permanently? This cannot be undone." />
    </div>
  );
}
